// Ring effects (materialize, glitch, flash core, shockwave, damage static, ownership sweep): one RingPool with a
// reserved slot range per registry entry, animated from the effect's data each frame.

import { Vector3, type Color } from 'three'
import { InvariantViolation } from '@taxwar/net'
import { effectsOf, type EffectId, type RingDef } from './effects'
import { tangentAt } from './paths'
import type { PointPlacement } from './placement'
import { RingPool } from './RingPool'
import { SlotRange, scaledCount } from './slots'
import { easeOut, envelopeAt } from './timing'
import type { VfxUniforms } from './uniforms'

/** A sweep ring has gone all the way round at this fraction of its life. */
const SWEEP_FILL_FRACTION = 0.5

export class RingSystem {
  readonly pool: RingPool
  private readonly ranges = new Map<EffectId, SlotRange>()
  private readonly defs: (RingDef | null)[]
  private readonly starts: Float64Array
  private readonly extents: Float32Array
  private readonly altitudes: Float32Array
  private readonly tangent = new Vector3()

  constructor(uniforms: VfxUniforms, scale: number) {
    let capacity = 0
    for (const [id, def] of effectsOf('ring')) {
      const count = scaledCount(def.maxConcurrent, scale)
      this.ranges.set(id, new SlotRange(capacity, count))
      capacity += count
    }
    this.pool = new RingPool(capacity, uniforms)
    this.defs = new Array<RingDef | null>(capacity).fill(null)
    this.starts = new Float64Array(capacity)
    this.extents = new Float32Array(capacity)
    this.altitudes = new Float32Array(capacity)
  }

  play(id: EffectId, def: RingDef, placement: PointPlacement, colour: Color, now: number): void {
    const range = this.ranges.get(id)
    if (range === undefined) throw new InvariantViolation(`ring effect ${id} has no reserved slots`)
    const slot = range.claim()
    this.pool.place(slot, placement.at, tangentAt(placement.at, this.tangent), def.style, def.dashes, def.glitch, Math.random(), colour, def.intensity)
    this.defs[slot] = def
    this.starts[slot] = now + def.delay
    this.extents[slot] = placement.extent
    this.altitudes[slot] = placement.altitude
  }

  update(now: number): void {
    for (let slot = 0; slot < this.defs.length; slot++) {
      const def = this.defs[slot]
      if (def === null || def === undefined) continue
      const age = now - (this.starts[slot] as number)
      if (age < 0) continue
      const t = age / def.duration
      if (t >= 1) {
        this.pool.hide(slot)
        this.defs[slot] = null
        continue
      }
      const extent = this.extents[slot] as number
      const outer = (def.radius[0] + (def.radius[1] - def.radius[0]) * easeOut(t)) * extent
      const altitude = (this.altitudes[slot] as number) + def.altitude[0] + (def.altitude[1] - def.altitude[0]) * t
      this.pool.animate(slot, outer * (1 - def.width), outer, altitude, envelopeAt(def.envelope, t), Math.min(1, t / SWEEP_FILL_FRACTION))
    }
    this.pool.upload()
  }

  dispose(): void {
    this.pool.dispose()
  }
}
